import type {
  SpartanProductEngineStagePalette,
  SpartanProductEngineTheme,
} from '@/app/lcv/components/sections/spartan/products/engine/types';
import type {
  SpartanTabsGlossIndicatorTone,
  SpartanTabsGlowIndicatorTone,
  SpartanTabsVariant,
} from '@/app/lcv/components/sections/spartan/shared/tabs/types';
import { cnva } from '@/app/utils/cnva';

export type EngineIndicator =
  | { kind: 'glow'; tone: SpartanTabsGlowIndicatorTone }
  | { kind: 'gloss'; tone: SpartanTabsGlossIndicatorTone };

export const getEngineTabsVariant = (
  theme: SpartanProductEngineTheme
): SpartanTabsVariant => (theme === 'light' ? 'dark-on-light' : 'dark');

/** Tab indicator from `tabPalette` (or `stagePalette` fallback). */
export const getEngineIndicator = (
  palette: SpartanProductEngineStagePalette
): EngineIndicator => {
  if (palette === 'lime') {
    return { kind: 'gloss', tone: 'lime' };
  }

  if (palette === 'blue') {
    return { kind: 'glow', tone: 'blue' };
  }

  if (palette === 'black') {
    return { kind: 'glow', tone: 'white' };
  }

  return { kind: 'glow', tone: 'orange' };
};

export const getEngineCtaColor = (
  theme: SpartanProductEngineTheme
): 'black' | 'white' => (theme === 'light' ? 'black' : 'white');

export const SpartanProductEngineTitleStyled = cnva(
  ['text-sub-h3 font-noto-bold'],
  {
    variants: {
      theme: {
        light: 'text-gray-900',
        dark: 'text-white',
      },
    },
    defaultVariants: {
      theme: 'dark',
    },
  }
);

export const SpartanProductEngineDescriptionStyled = cnva(['text-body-1'], {
  variants: {
    theme: {
      light: 'text-gray-700',
      dark: 'text-white',
    },
  },
  defaultVariants: {
    theme: 'dark',
  },
});

export const SpartanProductEngineRemarkStyled = cnva(['text-caption mt-2'], {
  variants: {
    theme: {
      light: 'text-gray-700',
      dark: 'text-white',
    },
  },
  defaultVariants: {
    theme: 'dark',
  },
});

export const SpartanProductEngineSpecStyled = cnva(
  ['xs:py-3 relative px-3 py-2'],
  {
    variants: {
      theme: {
        light: 'text-gray-900',
        dark: 'text-white',
      },
    },
    defaultVariants: {
      theme: 'dark',
    },
  }
);

export const SpartanProductEngineSpecLabelStyled = cnva(['text-body-2-fixed'], {
  variants: {
    theme: {
      light: 'text-gray-700',
      dark: 'text-gray-50',
    },
  },
  defaultVariants: {
    theme: 'dark',
  },
});

export const SpartanProductEngineSpecValueStyled = cnva(
  ['text-sub-h1 font-noto-bold'],
  {
    variants: {
      theme: {
        light: 'text-gray-900',
        dark: 'text-white',
      },
    },
    defaultVariants: {
      theme: 'dark',
    },
  }
);

export const SpartanProductEngineSpecUnitStyled = cnva(
  ['text-body-2 font-noto-bold'],
  {
    variants: {
      theme: {
        light: 'text-gray-900',
        dark: 'text-white',
      },
    },
    defaultVariants: {
      theme: 'dark',
    },
  }
);
